<template>
  <div id="cart-overlay" :class="[{ 'show': showCartItems }]">
    <div
      id="cc-cart-contents"
      :class="['position-absolute bg-white p-3', { 'show': showCartItems }]">
      <header class="d-flex align-items-center justify-content-between mb-4" ref="cartHeaderContainer">
        <h3 class="mb-0">Customer's Order</h3>
        <b-button @click="hideCartItems" class="p-0 bg-transparent border-0 shadow-none">
          <img src="@/assets/images/icons/close.svg" alt="Close">
        </b-button>
      </header>
      <template v-if="!loading.cart && cartItems && cartItems.list.length > 0">
        <div class="scrollable-cart-contents" ref="cartScrollableContainer">
          <template v-for="(branch, branchIndex) in branchesCollection">
            <p class="branch-name p-2" :key="`branch-name-${branchIndex}`"> {{ cartGroupedByBranch[branch][0].branch_name }}</p>
            <CartItemTemplate
              v-for="(item, index) in cartGroupedByBranch[branch][0].items"
              :key="`cart-item-${index}-${branchIndex}`"
              :orderId="`cart-item-${index}`"
              :item-uuid="item.uuid"
              :branch-uuid="branch"
              :ref="`cart-item-template-${index}-${branchIndex}`">
              <template slot="productName">{{ item.product_inventory.product_details.name }}</template>
              <template slot="productAttributes" v-if="item.product_inventory.product_details.type === 'variable'">{{ item.product_inventory.decoded_variation_code | commaSeparatedAttributes }}</template>
              <template slot="productAddOns" v-if="item.add_on_uuids.length > 0">{{ item.add_ons | commaSeparatedAddOns }}</template>
              <template slot="productQty">{{ Number(item.quantity) }}</template>
              <template slot="productPrice">&#8369; {{ item.total_amount | currency }}</template>
            </CartItemTemplate>
          </template>
        </div>
        <b-row class="py-3 cart-fees" ref="feesContainer">
          <b-col md="6">Subtotal</b-col>
          <b-col md="6" class="text-left text-md-right">&#8369; {{ cartItems.cart_total_amount | currency }}</b-col>
          <template
            v-for="(fee, index) in cartItems.fees">
            <b-col md="6" :key="`cart-fee-${index}-left`" v-if="fee.name.replace(/_/g, ' ') != 'Delivery fee'">{{ fee.name.replace(/_/g, " ") }}</b-col>
            <b-col md="6" :key="`cart-fee-${index}-right`" class="text-left text-md-right" v-if="fee.name.replace(/_/g, ' ') != 'Delivery fee'">&#8369; {{ fee.calculated_amount | currency }}</b-col>
          </template>
          <b-col md="12" class="border-bottom m-3"></b-col>
          <b-col md="6" class="fee-total">Total</b-col>
          <b-col md="6" class="fee-total text-left text-md-right">&#8369; {{ cartItems.computed_total | currency }}</b-col>
        </b-row>
      </template>
      <div v-else class="p-2 text-center text-muted">
        Cart Empty
      </div>
      <div class="generate-cartlink-btn-container border-top position-absolute w-100 p-3 text-left text-md-right" ref="generateContainer">
        <b-button
          variant="primary"
          class="pl-3 pr-5 py-2"
          :disabled="loading.checkout_link || (cartItems && cartItems.list.length === 0)"
          @click="generateCheckoutLink">
          Generate Checkout
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import CartItemTemplate from "./CartItemTemplate";
import _ from "lodash";

export default {
  components: { CartItemTemplate },
  computed: {
    ...mapGetters({
      showCartItems: "conversationalCommerce/showCartItems",
      cartItems: "conversationalCommerce/cartItems",
      loading: "conversationalCommerce/loading",
      viewProduct: "conversationalCommerce/viewProduct",
      links: "conversationalCommerce/links",
      errors: "conversationalCommerce/errors"
    }),
    ...mapState("auth", ["merchantDetails"]),
    cartGroupedByBranch: {
      get() {
        if (this.cartItems.list.length > 0) return _.groupBy(this.cartItems.list, 'branch_uuid');
        else return [];
      },
      set(list) {
        return list;
      }
    },
    branchesCollection: {
      get() {
        if (!_.isEmpty(this.cartGroupedByBranch)) return Object.keys(this.cartGroupedByBranch);
      },
      set(branchUuids) {
        return branchUuids;
      }
    }
  },
  filters: {
    currency(value) {
      if (!value) return 0;

      return Number(parseFloat(value).toFixed(2)).toLocaleString("en", {
        minimumFractionDigits: 2,
      });
    },
    commaSeparatedAttributes(attributes) {
      return attributes.map(attribute => attribute.value).join(", ");
    },
    commaSeparatedAddOns(addOns) {
      return addOns.map(addOn => addOn.name).join(", ");
    }
  },
  methods: {
    hideCartItems() {
      this.$store.commit("conversationalCommerce/setShowCartItems", false);
    },
    async generateCheckoutLink() {
      await this.$store.dispatch("conversationalCommerce/getCheckoutLink");
      if (_.isEmpty(this.errors) || !this.errors) {
        const { checkout } = this.links;
        const sendMessagePayload = `
          Checkout Link: <a href='${checkout.url}' target='_blank'>${checkout.url}</a><br/>
          Checkout Link Validate: ${this.$moment(checkout.validity).format("MMM DD, YYYY h:mm:ss A")}
        `;
        await this.$store.dispatch("conversationalCommerce/sendMessage", sendMessagePayload);
        if (_.isEmpty(this.errors) || !this.errors) this.$root.$emit("startCheckingOrderStatus");
      }
    },
    computeCartItemWidths() {
      const collectWidths = [];
      Object.keys(this.$refs).forEach(ref => {
        if (ref.includes('cart-item-template')) collectWidths.push(this.$refs[ref][0].$el.firstChild.clientWidth);
      });
      this.$nextTick(() => {
        const smallestWidth = Math.min.apply(Math, collectWidths);
        const itemQuantityContainers = document.getElementsByClassName('quantity-count');
        const widestItemQuantity = Math.max.apply(Math, Object.values(itemQuantityContainers).map(itemQuantity => itemQuantity.clientWidth));
        Object.keys(this.$refs).forEach(ref => {
          if (ref.includes('cart-item-template')) this.$refs[ref][0].$el.firstChild.style.maxWidth = `${smallestWidth}px`;
        });
        Object.values(itemQuantityContainers).map(itemQuantity => {
          itemQuantity.style.width = `${widestItemQuantity}px`;
        })
      });
    }
  },
  watch: {
    showCartItems(isShow) {
      if (isShow) {
        document.body.style.overflowY = "hidden";
        const getTotalHeight = ['cartHeaderContainer', 'generateContainer', 'feesContainer'];
        let totalHeight = 0;
        getTotalHeight.forEach(part => {
          if (this.$refs[part]) totalHeight = totalHeight += this.$refs[part].clientHeight;
        });
        this.$nextTick(() => {
          if (this.$refs['cartScrollableContainer']) this.$refs['cartScrollableContainer'].style.maxHeight = `calc(100vh - ${totalHeight + 50}px)`;
          this.computeCartItemWidths();
        });
      } else document.body.style.overflowY = null;
    }
  }
}
</script>

<style lang="scss" scoped>
#cart-overlay {
  background: rgba(0, 0, 0, 0.5);
  width: 100vw;
  top: 0;
  left: 0;
  opacity: 0;
  transition: all 0.2s ease-in;
  &.show {
    opacity: 1;
    position: fixed;
    height: 100vh;
  }
}
#cc-cart-contents {
  width: 400px;
  height: 100vh;
  right: -400px;
  top: 0;
  transition: all 0.3s ease-in;
  &.show {
    right: 0;
  }
  h3 {
    font-family: MainFontBold, sans-serif;
    font-weight: normal;
    font-size: 20px;
    color: #2F3941;
  }
  .branch-name {
    background-color: #f8f9f9;
  }
  .scrollable-cart-contents {
    overflow-y: auto;
  }
  .cart-fees {
    font-size: 14px;
    color: #2F3941;
    .fee-total {
      font-family: MainFontBold, sans-serif;
      font-weight: normal;
      font-size: 18px;
    }
  }
  .generate-cartlink-btn-container {
    margin-left: -1rem;
    bottom: 0;
    & > .btn.btn-primary {
      font-size: 16px;
      background-image: url('~@/assets/images/icons/arrow-forward.svg');
      background-repeat: no-repeat;
      background-position: center right 1rem;
      border-radius: 30px;
    }
  }
}
</style>
